import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { shippingApi } from '../api/shippingApi';
import { buildPeriodApiFilters, fiscalPeriodInfo } from '../utils/fiscalPeriod';
import { resetFiltersToDefault } from '../utils/filterReset';
import {
  SUPPLIER_SHARE_EDIT_DEFAULT_FILTERS,
  buildSupplierShareEditFiltersFromSearchParams,
} from '../constants/supplierShareFilters';
import { HacchuSplitPlanInlineEditor } from '../features/shipping/HacchuSplitPlanInlineEditor';
import { patchItemsSplitPlans } from '../features/shipping/splitPlanUtils';
import { SupplierStockEditor } from '../features/shipping/SupplierStockEditor';
import { ShipmentOriginCell } from '../features/shipping/ShipmentOriginCell';
import {
  buildSupplierShareColumns,
  buildSupplierSharePrintColumns,
} from '../features/shipping/supplierShareColumnDefs';
import { todayShipRowClassName, isTodayShipShareRow } from '../features/shipping/todayShipShare';
import { ShipRequestListPanel } from '../features/shipping/ShipRequestListPanel';
import { ShipRequestPreviewModal } from '../features/shipping/ShipRequestPreviewModal';
import { useSupplierAuth } from '../contexts/SupplierAuthContext';
import {
  PRINT_PAGE_TITLE_COUNT,
  PRINT_PAGE_TITLE_H1,
  PRINT_PAGE_TITLE_ROW,
  PRINT_SCROLL_CONTAINER,
  PRINT_SEARCH_ROW_TH,
  PRINT_SEARCH_ROW_TR,
  PRINT_TABLE,
  PRINT_TD_COMPOSITE,
  PRINT_TD_LEFT,
  PRINT_TD_NUM,
  PRINT_THEAD,
  PRINT_TH_LEFT,
  PRINT_TH_RIGHT,
  PRINT_TR_BODY,
  SUPPLIER_SHARE_PRINT_COL_WIDTHS,
  SUPPLIER_SHARE_PRINT_COMPOSITE_KEYS,
} from '../constants/printTableStyles';
import { formatDateTime } from '../utils/formatDateTime';

const SUPPLIER_CODE_TAKARA = 1576;
const SUPPLIER_CODE_DAIKURE = 2146;

const SupplierShareEditPage = () => {
  const { isAuthenticated, sessionChecked, supplierCode, supplierName, logout } = useSupplierAuth();
  const supplierCodeNum = supplierCode != null ? Number(supplierCode) : null;
  const isTakara = supplierCodeNum === SUPPLIER_CODE_TAKARA;
  const isDaikure = supplierCodeNum === SUPPLIER_CODE_DAIKURE;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPrintView = searchParams.get('print_view') === '1';
  const PRINT_MAX_COUNT = 1000;
  const PRINT_WARNING_THRESHOLD = 200;
  
  const fiscalMeta = useMemo(() => fiscalPeriodInfo(), []);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [savingStates, setSavingStates] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [requestPreviewNo, setRequestPreviewNo] = useState(null);
  const [listMode, setListMode] = useState('orders'); // orders | requests
  const debounceTimers = useRef({});
  const offsetRef = useRef(0);
  const observerTarget = useRef(null);
  const observerRef = useRef(null);
  const dueDateInputRefs = useRef({});
  const itemsRef = useRef([]);
  
  // フィルタ状態（URLパラメータから初期化）
  const initialFilters = buildSupplierShareEditFiltersFromSearchParams(searchParams);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  // 宝機材ログイン時のみ: 発注明細 / 在庫入力 タブ（localStorage で保持）
  const [editTab, setEditTab] = useState(() => {
    if (typeof window === 'undefined') return 'details';
    return window.localStorage.getItem('supplierShareEdit.takaraTab') || 'details';
  });
  
  // 最新の状態を参照するためのref
  const stateRef = useRef({ loadingMore: false, hasMore: true, loading: true });
  const appliedFiltersRef = useRef(appliedFilters);
  
  useEffect(() => {
    stateRef.current = { loadingMore, hasMore, loading };
  }, [loadingMore, hasMore, loading]);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // 印刷用ビュー表示時は body にクラスを付与し、印刷時に親のスクロール制約を解除する
  useEffect(() => {
    if (isPrintView) {
      document.body.classList.add('shipping-print-view');
    }
    return () => {
      document.body.classList.remove('shipping-print-view');
    };
  }, [isPrintView]);

  useEffect(() => {
    if (sessionChecked && !isAuthenticated) {
      navigate('/supplier-login');
      return;
    }
  }, [sessionChecked, isAuthenticated, navigate]);

  // URLパラメータを更新
  const updateSearchParams = (newFilters) => {
    const params = new URLSearchParams();
    if (newFilters.productQuery) {
      params.set('product_query', newFilters.productQuery);
    }
    if (newFilters.customerName) {
      params.set('customer_name', newFilters.customerName);
    }
    if (newFilters.startDate) {
      params.set('start_date', newFilters.startDate);
    }
    if (newFilters.endDate) {
      params.set('end_date', newFilters.endDate);
    }
    if (newFilters.supplierResponseDueDateNullOnly) {
      params.set('supplier_response_due_date_null_only', 'true');
    }
    if (newFilters.supplierResponseDueDateSetOnly) {
      params.set('supplier_response_due_date_set_only', 'true');
    }
    if (newFilters.shipRequestIssuedOnly) {
      params.set('ship_request_issued_only', 'true');
    }
    if (newFilters.hacchuDateFromToday) {
      params.set('hacchu_date_from_today', 'true');
    }
    // 期のフィルタ
    if (newFilters.currentPeriodOnly) {
      params.set('current_period_only', 'true');
    } else if (newFilters.previousPeriodsOnly) {
      params.set('previous_periods_only', 'true');
    }
    if (newFilters.ogakiFactoryOnly) {
      params.set('ogaki_factory_only', 'true');
    }
    if (newFilters.todayShipOnly) {
      params.set('today_ship_only', 'true');
    }
    // 両方falseの場合はパラメータに含めない（全期間表示）
    setSearchParams(params, { replace: true });
  };

  const loadInitialItems = useCallback(async () => {
    try {
      setLoading(true);
      offsetRef.current = 0;
      // 印刷用ビューでは印刷上限件数まで一括取得し、それ以外は無限スクロール用に100件ずつ取得
      const initialLimit = isPrintView ? PRINT_MAX_COUNT : 100;
      // 期フィルタを適用
      const filtersWithPeriod = buildPeriodApiFilters(appliedFilters);
      let data = await shippingApi.getSupplierShareList(initialLimit, 0, filtersWithPeriod);
      data = data.data || data;
      
      // 発注日起点のフィルタリング（今日以降）
      if (appliedFilters.hacchuDateFromToday) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // 発注日文字列をパースするヘルパー関数
        const parseHacchuDate = (dateStr) => {
          if (!dateStr || dateStr === '-') return null;
          
          // "24/01/15(月)" 形式の場合
          const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{2})/);
          if (match) {
            const year = 2000 + parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const day = parseInt(match[3], 10);
            return new Date(year, month, day);
          }
          
          // ISO形式やその他の形式を試す
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date;
          }
          
          return null;
        };
        
        data = data.filter(item => {
          const hacchuDate = parseHacchuDate(item.hacchu_date);
          if (!hacchuDate) {
            return false; // 発注日がない場合は除外
          }
          
          hacchuDate.setHours(0, 0, 0, 0);
          return hacchuDate >= today;
        });
        
        // 発注日が古い順にソート
        data.sort((a, b) => {
          const dateA = parseHacchuDate(a.hacchu_date);
          const dateB = parseHacchuDate(b.hacchu_date);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA - dateB; // 古い順（昇順）
        });
      }
      
      setItems(data);
      offsetRef.current = data.length;
      setHasMore(data.length === 100);
      // 最終更新日時を更新
      setLastUpdated(new Date());
    } catch (error) {
      console.error('仕入先共有一覧取得エラー:', error);
      alert('仕入先共有一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, isPrintView]);

  useEffect(() => {
    if (isAuthenticated) {
      loadInitialItems();
    }
  }, [isAuthenticated, loadInitialItems]);

  const loadMoreItems = async () => {
    const currentState = stateRef.current;
    if (currentState.loadingMore || !currentState.hasMore || currentState.loading) {
      return;
    }

    try {
      const currentOffset = offsetRef.current;
      const currentAppliedFilters = appliedFiltersRef.current;
      setLoadingMore(true);
      // 期フィルタを適用
      const filtersWithPeriod = buildPeriodApiFilters(currentAppliedFilters);
      let data = await shippingApi.getSupplierShareList(100, currentOffset, filtersWithPeriod);
      data = data.data || data;
      const originalDataLength = data.length; // フィルタリング前の件数を保持
      
      // 発注日起点のフィルタリング（今日以降）
      if (currentAppliedFilters.hacchuDateFromToday) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // 発注日文字列をパースするヘルパー関数
        const parseHacchuDate = (dateStr) => {
          if (!dateStr || dateStr === '-') return null;
          
          // "24/01/15(月)" 形式の場合
          const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{2})/);
          if (match) {
            const year = 2000 + parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const day = parseInt(match[3], 10);
            return new Date(year, month, day);
          }
          
          // ISO形式やその他の形式を試す
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date;
          }
          
          return null;
        };
        
        data = data.filter(item => {
          const hacchuDate = parseHacchuDate(item.hacchu_date);
          if (!hacchuDate) {
            return false; // 発注日がない場合は除外
          }
          
          hacchuDate.setHours(0, 0, 0, 0);
          return hacchuDate >= today;
        });
      }
      
      // フィルタリング後のデータが0件の場合、次のAPI呼び出しをスキップするために
      // offsetRefを更新して、hasMoreをfalseにする
      if (data.length === 0) {
        // フィルタリング後のデータが0件の場合でも、バックエンドから取得した件数でoffsetを更新
        offsetRef.current = currentOffset + originalDataLength;
        // バックエンドから取得した件数が100件未満の場合は、これ以上データがない
        if (originalDataLength < 100) {
          setHasMore(false);
        }
        // データが0件の場合は追加しない
      } else {
        setItems(prevItems => [...prevItems, ...data]);
        // offsetRefはフィルタリング前の件数で更新（バックエンドから取得した件数）
        offsetRef.current = currentOffset + originalDataLength;
        // hasMoreはバックエンドから取得した件数（フィルタリング前）で判定
        setHasMore(originalDataLength === 100);
      }
    } catch (error) {
      console.error('追加データ取得エラー:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // items.lengthが0の場合は、observerTargetが存在しないのでスキップ
    if (items.length === 0) {
      return;
    }
    
    // オブザーバーは一度だけ作成
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;

          const currentState = stateRef.current;
          
          if (entry.isIntersecting && currentState.hasMore && !currentState.loadingMore && !currentState.loading) {
            loadMoreItems();
          }
        },
        { 
          threshold: 0,
          rootMargin: '500px'
        }
      );
    }

    const observer = observerRef.current;

    // observerTargetが存在するまで待機してからオブザーバーを設定
    const setupObserver = () => {
      const target = observerTarget.current;
      if (target) {
        // 既に監視中の場合は一度解除してから再設定（確実に監視を開始するため）
        const isObserving = target.getAttribute('data-observing') === 'true';
        if (isObserving) {
          observer.unobserve(target);
          target.removeAttribute('data-observing');
        }
        // 少し待ってから再設定（DOM更新を待つ）
        const timeoutId = setTimeout(() => {
          const currentTarget = observerTarget.current;
          if (currentTarget && !currentTarget.getAttribute('data-observing')) {
            observer.observe(currentTarget);
            currentTarget.setAttribute('data-observing', 'true');
          }
        }, 50);
        return { timeoutId };
      }
      return null;
    };

    // 即座にチェック
    let setupResult = setupObserver();
    let timeoutId1 = null;
    let timeoutId2 = null;
    let timeoutId3 = null;

    if (!setupResult) {
      // 少し待ってから再試行（DOMが更新されるのを待つ）
      timeoutId1 = setTimeout(() => {
        setupResult = setupObserver();
        if (!setupResult) {
          // もう一度試行
          timeoutId2 = setTimeout(() => {
            setupResult = setupObserver();
            if (!setupResult) {
              // 最後の試行
              timeoutId3 = setTimeout(() => {
                setupObserver();
              }, 300);
            }
          }, 300);
        }
      }, 300);
    }

    return () => {
      if (setupResult?.timeoutId) clearTimeout(setupResult.timeoutId);
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
      if (timeoutId3) clearTimeout(timeoutId3);
      const target = observerTarget.current;
      if (target && target.hasAttribute('data-observing')) {
        observer.unobserve(target);
        target.removeAttribute('data-observing');
      }
    };
  }, [hasMore, items.length, isAuthenticated]); // hasMore、items.length、isAuthenticatedを依存配列に追加

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString();
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return price.toLocaleString('ja-JP', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getItemKey = (item) => {
    return `${item.hacchu_code}-${item.hacchu_changecnt}-${item.hacchu_datacode}`;
  };

  const handleRowClick = (item, e) => {
    // 入力フィールドのクリックは展開をトリガーしない
    if (e.target.tagName === 'INPUT' || e.target.closest('input')) {
      return;
    }
    
    const key = getItemKey(item);
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(key)) {
      // 既に開いている場合は閉じる
      newExpandedRows.delete(key);
    } else {
      // 新しい行を開く場合は、既存の展開をすべて閉じてから開く
      newExpandedRows.clear();
      newExpandedRows.add(key);
    }
    
    setExpandedRows(newExpandedRows);
  };

  const handleSplitPlanDataChange = useCallback((rowId, splitPlans) => {
    setItems((prevItems) =>
      patchItemsSplitPlans(prevItems, rowId, getItemKey, splitPlans, [
        'latest_shipment_date',
        'latest_supplier_response_due_date',
      ])
    );
  }, []);

  const handleDateChange = useCallback(async (item, field, value, rowIndex) => {
    const key = getItemKey(item);
    
    // デバウンス処理
    if (debounceTimers.current[key + field]) {
      clearTimeout(debounceTimers.current[key + field]);
    }

    // ローカル状態を即座に更新（予定納期は表示用 latest も揃え、リロード前から正しく表示されるようにする）
    setItems(prevItems =>
      prevItems.map(i => {
        if (getItemKey(i) !== key) return i;
        const updated = { ...i, [field]: value || null };
        if (field === 'supplier_response_due_date' && (!i.split_plans || i.split_plans.length === 0)) {
          updated.latest_supplier_response_due_date = value || null;
        }
        return updated;
      })
    );

    // 保存状態を設定
    setSavingStates(prev => ({ ...prev, [key + field]: true }));

    debounceTimers.current[key + field] = setTimeout(async () => {
      try {
        await shippingApi.updateShipmentPlan(
          item.hacchu_code,
          item.hacchu_changecnt,
          item.hacchu_datacode,
          { [field]: value || null }
        );
        setSavingStates(prev => ({ ...prev, [key + field]: false }));

        // 予定納期を入力した場合、次の行（編集可能な行）のカレンダーを開く（行・既存値の有無で同じ挙動にするため遅延＋1回リトライ）
        if (field === 'supplier_response_due_date' && value && rowIndex != null) {
          const currentItems = itemsRef.current;
          const nextIndex = rowIndex + 1;
          if (nextIndex < currentItems.length) {
            const nextRow = currentItems[nextIndex];
            const nextHasSplitPlans = nextRow.split_plans && nextRow.split_plans.length > 0;
            const nextIsStock = !!nextRow.supplier_stock_flag;
            if (!nextHasSplitPlans && !nextIsStock) {
              const nextKey = `supplier_response_due_date-${nextRow.hacchu_code}-${nextRow.hacchu_changecnt}-${nextRow.hacchu_datacode}`;
              const focusNextDueDateInput = () => {
                const nextInput = dueDateInputRefs.current[nextKey];
                if (!nextInput) return false;
                nextInput.focus();
                if (nextInput.showPicker && typeof nextInput.showPicker === 'function') {
                  try {
                    nextInput.showPicker();
                  } catch {
                    nextInput.click();
                  }
                } else {
                  nextInput.click();
                }
                return true;
              };
              setTimeout(() => {
                if (!focusNextDueDateInput()) {
                  setTimeout(() => focusNextDueDateInput(), 200);
                }
              }, 250);
            }
          }
        }
      } catch (error) {
        console.error('保存エラー:', error);
        alert('予定納期の保存に失敗しました');
        setSavingStates(prev => ({ ...prev, [key + field]: false }));
        // エラー時は再読み込み
        loadInitialItems();
      }
    }, 500);
  }, [loadInitialItems]);

  const handleSupplierStockToggle = useCallback(async (item, newValue) => {
    const key = getItemKey(item);

    // ローカル状態を即座に更新（在庫フラグと予定納期のクリア）
    setItems(prevItems =>
      prevItems.map(i => {
        if (getItemKey(i) === key) {
          const updated = { ...i, supplier_stock_flag: newValue };
          if (newValue) {
            // 在庫扱いの場合は予定納期をクリア
            updated.supplier_response_due_date = null;
            // 分納計画がない場合は latest もクリア
            if (!i.split_plans || i.split_plans.length === 0) {
              updated.latest_supplier_response_due_date = null;
            }
          }
          return updated;
        }
        return i;
      })
    );

    try {
      await shippingApi.updateShipmentPlan(
        item.hacchu_code,
        item.hacchu_changecnt,
        item.hacchu_datacode,
        {
          supplier_stock_flag: newValue,
          // バックエンド側でも在庫ON時に予定納期をクリアするが、
          // ここでは明示的にnullを送らず、既存の「Noneは変更なし」の挙動を維持する
        }
      );
    } catch (error) {
      console.error('在庫フラグ更新エラー:', error);
      alert('在庫フラグの更新に失敗しました');
      // 失敗時は再読み込みして整合性を保つ
      loadInitialItems();
    }
  }, [loadInitialItems]);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    
    // 予定納期フィルタの排他制御
    if (field === 'supplierResponseDueDateNullOnly' && value === true) {
      newFilters.supplierResponseDueDateSetOnly = false;
    } else if (field === 'supplierResponseDueDateSetOnly' && value === true) {
      newFilters.supplierResponseDueDateNullOnly = false;
    }
    
    setFilters(newFilters);
    // 検索ボタンクリック時のみ適用（参照画面・発送予定ページと同様）
  };

  const handleFilterClear = (field) => {
    const newFilters = { ...filters, [field]: typeof filters[field] === 'boolean' ? false : '' };
    setFilters(newFilters);
    // 検索ボタンクリック時のみ適用（参照画面・発送予定ページと同様）
  };

  const handleAllFiltersClear = () => {
    resetFiltersToDefault({
      defaults: SUPPLIER_SHARE_EDIT_DEFAULT_FILTERS,
      setFilters,
      setAppliedFilters,
      updateSearchParams,
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
    updateSearchParams(filters);
  };

  const handleLogout = () => {
    logout();
    navigate('/supplier-login');
  };

  // 発送元更新（ダイクレログイン時）
  const handleShipmentOriginUpdate = async (row, origin) => {
    try {
      await shippingApi.updateShipmentPlan(
        row.hacchu_code,
        row.hacchu_changecnt,
        row.hacchu_datacode,
        { shipment_origin: origin }
      );
      setItems(prev => prev.map(item =>
        item.hacchu_code === row.hacchu_code &&
        item.hacchu_changecnt === row.hacchu_changecnt &&
        item.hacchu_datacode === row.hacchu_datacode
          ? { ...item, shipment_origin: origin }
          : item
      ));
    } catch (error) {
      console.error('発送元更新エラー:', error);
      alert('発送元の更新に失敗しました');
    }
  };

  // メモは社内参照側のかたまり編集で管理。仕入先画面は閲覧のみ。
  const columnsWithOrigin = useMemo(() => {
    const cols = buildSupplierShareColumns('edit', {
      formatDate,
      formatNumber,
      includeStock: true,
      includeShipmentOrigin: isDaikure,
      onHacchuNoClick: (hacchuNo, row) => {
        const no = row?.latest_ship_request_no;
        if (no) {
          setRequestPreviewNo(no);
        } else {
          window.alert('この発注にはまだ発行済みの依頼書がありません。');
        }
      },
      MemoCell: ({ row }) => (
        <div
          className="min-h-[24px] w-full max-w-[200px] truncate text-slate-900"
          title={row.supplier_share_memo || ''}
          onClick={(e) => e.stopPropagation()}
        >
          {row.supplier_share_memo || <span className="text-slate-400 text-xs">-</span>}
        </div>
      ),
      StockCell: ({ row }) => {
        const hasSplitPlans = row.split_plans && row.split_plans.length > 0;
        const isStock = !!row.supplier_stock_flag;
        if (hasSplitPlans) {
          return (
            <span className="inline-flex items-center px-2 py-1 text-xs rounded-full border border-slate-300 text-slate-700 bg-slate-50">
              {isStock ? '在庫' : '-'}
            </span>
          );
        }
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleSupplierStockToggle(row, !isStock);
            }}
            className={`px-3 py-1 text-xs rounded-full border ${
              isStock
                ? 'bg-emerald-600 border-emerald-600 text-white'
                : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            在庫
          </button>
        );
      },
      ShipmentOriginCell: ({ row }) => (
        <ShipmentOriginCell
          value={row.shipment_origin || null}
          onChange={(origin) => handleShipmentOriginUpdate(row, origin)}
        />
      ),
    });
    return cols.map((col) => {
      if (col.field !== 'supplier_response_due_date') return col;
      return {
        ...col,
        render: (row, rowIdx) => {
          const displayDate = row.latest_supplier_response_due_date || row.supplier_response_due_date;
          const hasSplitPlans = row.split_plans && row.split_plans.length > 0;
          const isStock = !!row.supplier_stock_flag;
          if (hasSplitPlans) {
            return <span className="text-slate-900">{formatDate(displayDate) || '-'}</span>;
          }
          if (isStock) {
            return <span className="text-slate-400 text-sm">在庫のため入力不要</span>;
          }
          const refKey = `supplier_response_due_date-${row.hacchu_code}-${row.hacchu_changecnt}-${row.hacchu_datacode}`;
          return (
            <div className="flex items-center gap-2">
              <input
                ref={(el) => {
                  if (el) {
                    dueDateInputRefs.current[refKey] = el;
                  } else {
                    delete dueDateInputRefs.current[refKey];
                  }
                }}
                type="date"
                value={formatDate(displayDate) || ''}
                onChange={(e) => handleDateChange(row, 'supplier_response_due_date', e.target.value, rowIdx)}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                onBlur={(e) => e.stopPropagation()}
                className="px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
              />
            </div>
          );
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDaikure]);

  const getPrintSearchSummary = () => {
    const parts = [];
    if (supplierName) parts.push(`仕入先=${supplierName}`);
    if (appliedFilters.productQuery) parts.push(`製品・寸法=${appliedFilters.productQuery}`);
    if (appliedFilters.customerName) parts.push(`得意先=${appliedFilters.customerName}`);
    if (appliedFilters.startDate) parts.push(`開始日=${appliedFilters.startDate}`);
    if (appliedFilters.endDate) parts.push(`終了日=${appliedFilters.endDate}`);
    if (appliedFilters.currentPeriodOnly) parts.push('今期のみ');
    if (appliedFilters.previousPeriodsOnly) parts.push('前期以前');
    if (appliedFilters.hacchuDateFromToday) parts.push('発注日今日以降');
    if (appliedFilters.supplierResponseDueDateNullOnly) parts.push('予定納期未設定');
    if (appliedFilters.supplierResponseDueDateSetOnly) parts.push('予定納期設定済');
    if (appliedFilters.shipRequestIssuedOnly) parts.push('発行済');
    if (appliedFilters.ogakiFactoryOnly) parts.push('大垣工場');
    if (appliedFilters.todayShipOnly) parts.push('本日発送');
    return parts.length ? `検索条件: ${parts.join(', ')}` : '検索条件: なし';
  };

  const renderPrintView = () => {
    const isStockTab = isTakara && editTab === 'stock';
    const sourceItems = isStockTab ? [] : items;
    const printItemsFiltered = sourceItems;
    const printCount = Math.min(printItemsFiltered.length, PRINT_MAX_COUNT);

    const printColumns = buildSupplierSharePrintColumns(formatDate, formatNumber);



    return (
      <div className="h-full flex flex-col bg-white print-view-root">
        {/* 画面用ヘッダー（印刷時は非表示） */}
        <div className="sticky top-0 z-20 page-section print:hidden">
          <div className="page-header">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 compact:text-lg">仕入先共有（編集・一覧印刷）</h1>
              <p className="meta-text mt-1">{getPrintSearchSummary()}</p>
              {isStockTab && (
                <p className="text-xs text-amber-700 mt-1">
                  在庫入力タブは印刷対象外です。発注明細タブをご利用ください。
                </p>
              )}
              {lastUpdated && (
                <p className="text-xs text-slate-500 mt-1">
                  最終更新: {formatDateTime(lastUpdated)}
                </p>
              )}
            </div>
            <div className="page-header-actions">
              <button
                type="button"
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.delete('print_view');
                  setSearchParams(params, { replace: true });
                }}
                className="btn-toolbar text-sm border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-100"
              >
                通常表示に戻る
              </button>
              <button
                type="button"
                onClick={loadInitialItems}
                disabled={loading}
                className="btn-toolbar text-sm border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-100 disabled:opacity-50"
              >
                再試行
              </button>
              <div className="meta-text">
                件数: {printCount}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (isStockTab) {
                    alert('在庫入力タブは印刷対象外です。発注明細タブに切り替えてください。');
                    return;
                  }
                  if (printCount > PRINT_MAX_COUNT) {
                    alert(`印刷上限の${PRINT_MAX_COUNT}件を超えています。条件を絞り込んでください。`);
                    return;
                  }
                  if (printCount === 0) {
                    alert('印刷対象データがありません。');
                    return;
                  }
                  if (printCount > PRINT_WARNING_THRESHOLD) {
                    const ok = window.confirm(
                      `現在の検索条件では ${printCount} 件を印刷しようとしています。本当に印刷してよいですか？`
                    );
                    if (!ok) return;
                  }
                  window.print();
                }}
                className="btn-toolbar bg-slate-700 text-white rounded-md text-sm hover:bg-slate-800"
              >
                印刷実行
              </button>
            </div>
          </div>
        </div>

        {/* 印刷時のみのタイトル */}
        <div className={PRINT_PAGE_TITLE_ROW}>
          <h1 className={PRINT_PAGE_TITLE_H1}>仕入先共有一覧（編集）</h1>
          <span className={PRINT_PAGE_TITLE_COUNT}>件数: {printCount}</span>
        </div>

        {/* テーブル本体 */}
        <div className={`${PRINT_SCROLL_CONTAINER} min-h-0`}>
          <table className={PRINT_TABLE}>
            <colgroup>
              {printColumns.map((col) => (
                <col key={col.key} style={{ width: SUPPLIER_SHARE_PRINT_COL_WIDTHS[col.key] || 'auto' }} />
              ))}
            </colgroup>
            <thead className={PRINT_THEAD}>
              <tr className={PRINT_SEARCH_ROW_TR}>
                <th colSpan={printColumns.length} className={PRINT_SEARCH_ROW_TH}>
                  {getPrintSearchSummary()}
                </th>
              </tr>
              <tr>
                {printColumns.map((col) => (
                  <th
                    key={col.key}
                    className={col.key === 'hacchu_amount' ? PRINT_TH_RIGHT : PRINT_TH_LEFT}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {printCount === 0 ? (
                <tr>
                  <td colSpan={printColumns.length} className="px-4 py-6 text-center text-slate-500 text-sm">
                    データがありません
                  </td>
                </tr>
              ) : (
                printItemsFiltered.slice(0, printCount).map((row, idx) => (
                  <tr
                    key={idx}
                    className={`${PRINT_TR_BODY}${isTodayShipShareRow(row) ? ' bg-rose-50' : ''}`}
                  >
                    {printColumns.map((col) => {
                      const tdClass =
                        col.key === 'hacchu_amount'
                          ? PRINT_TD_NUM
                          : SUPPLIER_SHARE_PRINT_COMPOSITE_KEYS.includes(col.key)
                            ? PRINT_TD_COMPOSITE
                            : PRINT_TD_LEFT;
                      return (
                        <td key={col.key} className={tdClass}>
                          {col.render(row)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!sessionChecked || !isAuthenticated) {
    return null;
  }

  if (isPrintView) {
    return renderPrintView();
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50 compact-content">
      <div className="page-section">
        <div className="page-header">
          <div>
            <h1 className="page-title">仕入先共有 - 編集</h1>
            {supplierName && (
              <p className="meta-text mt-1">ログイン中: {supplierName}</p>
            )}
          </div>
          <div className="page-header-actions">
            {lastUpdated && (
              <span className="meta-text">
                最終更新: {formatDateTime(lastUpdated)}
              </span>
            )}
            <button
              onClick={loadInitialItems}
              disabled={loading}
              className="flex items-center gap-2 btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              更新
            </button>
            <button
              type="button"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('print_view', '1');
                setSearchParams(params, { replace: true });
              }}
              className="flex items-center gap-2 btn-toolbar bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors font-medium text-sm border border-slate-300"
            >
              一覧印刷
            </button>
            <button
              onClick={handleLogout}
              className="btn-toolbar bg-slate-500 text-white rounded-md hover:bg-slate-600 transition-colors font-medium text-sm"
            >
              ログアウト
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-slate-200 page-section-x">
        <div className="flex space-x-1 pt-2">
          <button
            type="button"
            onClick={() => setListMode('orders')}
            className={`px-4 py-2 text-sm font-medium rounded-t-md ${
              listMode === 'orders' ? 'bg-slate-800 text-white' : 'text-slate-600'
            }`}
          >
            発注一覧
          </button>
          <button
            type="button"
            onClick={() => setListMode('requests')}
            className={`px-4 py-2 text-sm font-medium rounded-t-md ${
              listMode === 'requests' ? 'bg-slate-800 text-white' : 'text-slate-600'
            }`}
          >
            依頼一覧
          </button>
        </div>
      </div>

      {listMode === 'requests' ? (
        <div className="page-section-x py-4 flex-1 overflow-auto">
          <ShipRequestListPanel mode="edit" />
        </div>
      ) : (
      <>

      {/* 宝機材ログイン時のみ: 発注明細 / 在庫入力 タブ */}
      {isTakara && (
        <div className="bg-white border-b border-slate-200 page-section-x">
          <div className="flex space-x-1">
            <button
              type="button"
              onClick={() => {
                setEditTab('details');
                try {
                  if (typeof window !== 'undefined') {
                    window.localStorage.setItem('supplierShareEdit.takaraTab', 'details');
                  }
                } catch (e) {
                  console.warn('Failed to persist editTab to localStorage', e);
                }
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded ${
                editTab === 'details'
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              発注明細
            </button>
            <button
              type="button"
              onClick={() => {
                setEditTab('stock');
                try {
                  if (typeof window !== 'undefined') {
                    window.localStorage.setItem('supplierShareEdit.takaraTab', 'stock');
                  }
                } catch (e) {
                  console.warn('Failed to persist editTab to localStorage', e);
                }
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded ${
                editTab === 'stock'
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              在庫入力
            </button>
          </div>
        </div>
      )}

      {/* フィルタバー（発注明細タブまたは宝機材以外のとき表示） */}
      {(!isTakara || editTab === 'details') && (
      <div className="page-section">
        <form onSubmit={handleFilterSubmit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">製品名・寸法</label>
              <input
                type="text"
                value={filters.productQuery}
                onChange={(e) => handleFilterChange('productQuery', e.target.value)}
                placeholder="製品名、寸法で検索"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">得意先名</label>
              <input
                type="text"
                value={filters.customerName}
                onChange={(e) => handleFilterChange('customerName', e.target.value)}
                placeholder="得意先名で検索"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">発注日（開始）</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">発注日（終了）</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
              />
            </div>
          </div>
          <div className="filter-toolbar">
            <div className="filter-group">
              <span className="text-xs text-slate-500 w-16 flex-shrink-0">発注日:</span>
              <button
                type="button"
                onClick={() => handleFilterChange('hacchuDateFromToday', !filters.hacchuDateFromToday)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.hacchuDateFromToday
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="発注日が今日以降"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>今日以降</span>
              </button>
            </div>
            
            {/* 期のフィルタ */}
            <div className="filter-group">
              <span className="text-xs text-slate-500 w-16 flex-shrink-0">期:</span>
              <button
                type="button"
                onClick={() => {
                  const newValue = !filters.currentPeriodOnly;
                  handleFilterChange('currentPeriodOnly', newValue);
                  if (newValue) {
                    // 今期のみをONにしたら、前期以前はOFFにする
                    handleFilterChange('previousPeriodsOnly', false);
                  }
                }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.currentPeriodOnly
                    ? 'bg-slate-700 text-white hover:bg-slate-800'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title={`今期（${fiscalMeta.current_ki}期）のみ`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>今期のみ</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  const newValue = !filters.previousPeriodsOnly;
                  handleFilterChange('previousPeriodsOnly', newValue);
                  if (newValue) {
                    // 前期以前をONにしたら、今期のみはOFFにする
                    handleFilterChange('currentPeriodOnly', false);
                  }
                }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.previousPeriodsOnly
                    ? 'bg-slate-700 text-white hover:bg-slate-800'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="前期以前のデータを表示"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>前期以前</span>
              </button>
            </div>
            <div className="filter-group">
              <span className="text-xs text-slate-500 w-16 flex-shrink-0">予定納期:</span>
              <button
                type="button"
                onClick={() => handleFilterChange('supplierResponseDueDateNullOnly', !filters.supplierResponseDueDateNullOnly)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.supplierResponseDueDateNullOnly
                    ? 'bg-amber-600 text-white hover:bg-amber-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="予定納期が未設定のデータのみ表示"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>未設定</span>
              </button>
              <button
                type="button"
                onClick={() => handleFilterChange('supplierResponseDueDateSetOnly', !filters.supplierResponseDueDateSetOnly)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.supplierResponseDueDateSetOnly
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="予定納期が設定済のデータのみ表示"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>設定済</span>
              </button>
            </div>
            <div className="filter-group">
              <span className="text-xs text-slate-500 w-16 flex-shrink-0">依頼書:</span>
              <button
                type="button"
                onClick={() => handleFilterChange('shipRequestIssuedOnly', !filters.shipRequestIssuedOnly)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.shipRequestIssuedOnly
                    ? 'bg-slate-700 text-white hover:bg-slate-800'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="依頼書発行済（一部含む）のみ表示"
              >
                <span>発行済</span>
              </button>
            </div>
            {/* 大垣工場フィルタ（宝機材ログイン時のみ） */}
            {isTakara && (
              <div className="filter-group">
                <span className="text-xs text-slate-500 w-16 flex-shrink-0">納入先:</span>
                <button
                  type="button"
                  onClick={() => handleFilterChange('ogakiFactoryOnly', !filters.ogakiFactoryOnly)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.ogakiFactoryOnly
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  title="納入先に「大垣工場」を含むデータのみ表示"
                >
                  <span>大垣工場</span>
                </button>
              </div>
            )}
            <div className="filter-group">
              <span className="text-xs text-slate-500 w-16 flex-shrink-0">備考:</span>
              <button
                type="button"
                onClick={() => handleFilterChange('todayShipOnly', !filters.todayShipOnly)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.todayShipOnly
                    ? 'bg-rose-600 text-white hover:bg-rose-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="備考に「本日発送」があり、依頼書の発行日が今日のデータのみ表示"
              >
                <span>本日発送</span>
              </button>
            </div>
            <div className="flex gap-2 ml-auto shrink-0 compact:w-full compact:justify-end">
              <button
                type="submit"
                className="btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm"
              >
                検索
              </button>
              <button
                type="button"
                onClick={handleAllFiltersClear}
                className="btn-toolbar bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors font-medium text-sm"
              >
                クリア
              </button>
            </div>
          </div>
        </form>
      </div>
      )}

      <div className="flex-1 overflow-hidden flex">
        <div className="flex-1 flex flex-col page-section-x pt-4 pb-2 min-w-0 min-h-0">
          {isTakara && editTab === 'stock' ? (
            <SupplierStockEditor supplierCode={supplierCodeNum} title="在庫入力" />
          ) : (
          <>
          <div className="mb-2 bg-white rounded-lg shadow-sm border border-slate-200 h-full flex flex-col">
                <div className="flex-1 overflow-auto">
                  <table className="divide-y divide-slate-200" style={{ minWidth: 'max-content', width: '100%' }}>
                    <thead className="bg-slate-50">
                      <tr>
                        {columnsWithOrigin.map((col, idx) => (
                          <th
                            key={idx}
                            className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10"
                          >
                            {col.header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={columnsWithOrigin.length} className="px-4 py-8 text-center text-slate-500">
                            データがありません
                          </td>
                        </tr>
                      ) : (
                        items.map((row, rowIdx) => {
                          const key = getItemKey(row);
                          const isExpanded = expandedRows.has(key);
                          return (
                            <React.Fragment key={key}>
                              <tr
                                onClick={(e) => handleRowClick(row, e)}
                                className={todayShipRowClassName(row)}
                              >
                                {columnsWithOrigin.map((col, colIdx) => (
                                  <td key={colIdx} className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                                    {col.render ? col.render(row, rowIdx) : row[col.field]}
                                  </td>
                                ))}
                              </tr>
                              {isExpanded && (
                                <tr>
                                  <td colSpan={columnsWithOrigin.length} className="px-4 py-4 bg-slate-50">
                                    <div className="bg-white rounded-lg border border-slate-200 p-4">
                                      <h3 className="font-semibold text-sm mb-3 text-slate-900">
                                        分納計画
                                      </h3>
                                      <HacchuSplitPlanInlineEditor
                                        hacchuCode={row.hacchu_code}
                                        hacchuChangecnt={row.hacchu_changecnt}
                                        hacchuDatacode={String(row.hacchu_datacode)}
                                        hacchuAmount={row.hacchu_amount}
                                        onDataChange={(splitPlans) => handleSplitPlanDataChange(key, splitPlans)}
                                        hideShipmentFields={true}
                                        initialSplitPlans={row.split_plans ?? []}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  {hasMore && items.length > 0 && (
                    <div ref={observerTarget} className="h-1 flex-shrink-0 bg-transparent" style={{ minHeight: '1px' }} />
                  )}
                </div>
              </div>
              {!hasMore && items.length > 0 && !loading && (
                <div className="text-center py-3 text-slate-500 text-sm bg-slate-50 border-t border-slate-200 rounded-b-lg flex-shrink-0">すべてのデータを表示しました</div>
              )}
          </>
          )}
        </div>
      </div>
      </>
      )}
      {loadingMore && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg shadow-lg">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm font-medium">追加データを読み込み中...</span>
          </div>
        </div>
      )}
      <ShipRequestPreviewModal
        isOpen={!!requestPreviewNo}
        requestNo={requestPreviewNo}
        onClose={() => setRequestPreviewNo(null)}
      />
    </div>
  );
};

export default SupplierShareEditPage;
